import Configuration from '@/agilis/util/Configuration'
import styled from 'vue-styled-components'
import validationMixin from '../utils/validationMixin'
import simpleInputWrapper from '../utils/simpleInputWrapper'
import hljs from 'highlight.js'
import Vue from 'vue'
import { loadImagesSrc, previewRender } from './utils'
import { mapActions, mapGetters } from 'vuex'
import L from 'lodash'

window.hljs = hljs
window.loadImagesSrc = loadImagesSrc

const SimpleMDE = () => import(/* webpackChunkName: "simplemde" */'simplemde')

const toolbar = (simplemde, formMarkdown) => {
  return [
    {
      name: 'bold',
      className: 'mdi mdi-format-bold',
      action: simplemde.toggleBold
    },
    {
      name: 'italic',
      className: 'mdi mdi-format-italic',
      action: simplemde.toggleItalic
    },
    {
      name: 'strikethrough',
      className: 'mdi mdi-format-strikethrough',
      action: simplemde.toggleStrikethrough
    },
    '|',
    {
      name: 'heading',
      className: 'mdi mdi-format-title',
      action: simplemde.toggleHeadingSmaller
    },
    {
      name: 'code',
      className: 'mdi mdi-code-tags',
      action: simplemde.toggleCodeBlock
    },
    {
      name: 'quote',
      className: 'mdi mdi-format-quote-close',
      action: simplemde.toggleBlockquote
    },
    {
      name: 'unordered-list',
      className: 'mdi mdi-format-list-bulleted',
      action: simplemde.toggleUnorderedList
    },
    {
      name: 'ordered-list',
      className: 'mdi mdi-format-list-numbered',
      action: simplemde.toggleOrderedList
    },
    {
      name: 'clean-block',
      className: 'mdi mdi-format-clear',
      action: simplemde.cleanBlock
    },
    '|',
    {
      name: 'link',
      className: 'mdi mdi-link',
      action: simplemde.drawLink
    },
    {
      name: 'image',
      className: 'mdi mdi-image',
      action: formMarkdown.trackerDTO &&
      formMarkdown.userCanAddAttachment
        ? formMarkdown.uploadImage
        : simplemde.drawImage
    },
    {
      name: 'table',
      className: 'mdi mdi-table',
      action: simplemde.drawTable
    },
    {
      name: 'horizontal-rule',
      className: 'mdi mdi-window-minimize',
      action: simplemde.drawHorizontalRule
    },
    '|',
    {
      name: 'fullscreen',
      className: 'mdi mdi-fullscreen no-disable no-mobile',
      action: simplemde.toggleFullScreen
    },
    {
      name: 'side-by-side',
      className: 'mdi mdi-view-column no-disable no-mobile',
      action: simplemde.toggleSideBySide
    },
    {
      name: 'preview',
      className: 'mdi mdi-eye no-disable',
      action: simplemde.togglePreview
    }
  ].filter(Boolean)
}

const FormMarkdownWrapperProps = {
  showPlaceholder: Boolean
}

const FormMarkdownWrapper = styled('div', FormMarkdownWrapperProps)`
  position: relative;

  .mdi {
    font-size: 20px;
    border-radius: 100%;
    border: none;
  }

  .mdi.active, .mdi:hover {
    background-color: var(--v-hover-base);
  }

  .vue-simplemde {
    width: 100%;
  }

  .vue-simplemde > textarea, .CodeMirror-scroll {
    height: 100% !important;
    min-height: 100% !important;
  }

  .CodeMirror {
    height: calc(100% - 3em) !important;
    min-height: calc(100% - 3em) !important;
    border-color: var(--v-border-base);
    border-radius: 0 !important;
  }

  .editor-toolbar, .editor-toolbar:hover {
    background-color: white;
    border-radius: 0;
    opacity: 1;
  }

  .editor-toolbar a {
    color: var(--v-primary-base) !important;
  }

  .editor-toolbar.disabled-for-preview a:not(.no-disable) {
    color: var(--v-border-darken1) !important;
  }

  .v-input__append-inner {
    position: absolute;
    padding: 0 !important;
    margin: 0 !important;
    inset: 0;
    overflow: hidden;
  }

  .v-label, .v-label--active {
    z-index: 2;
  }

  .v-label--active {
    transform: translateY(-66px) scale(0.75) !important;
  }

  fieldset {
    z-index: 2;
  }

  legend {
    background-color: white;
  }

  .v-text-field__slot > textarea {
    opacity: 0;
  }

  .v-text-field__slot {
    margin-top: 50px;
  }

  fieldset.primary--text {
    border: 2px solid currentColor;
  }

  fieldset.primary--text ~ .v-text-field__slot .v-label {
    color: var(--v-primary-base);
  }

  .v-input .v-progress-linear {
    top: 100%;
  }

  ${props => props.showPlaceholder ? '' : `
    .CodeMirror-placeholder {
      display: none
    }
  `}
`

export default {
  name: 'FormMarkdown',
  mixins: [simpleInputWrapper, validationMixin],
  props: {
    height: {
      type: String,
      default: '300'
    },
    rows: String,
    trackerDTO: Object,
    placeholder: String
  },
  components: {
    VueSimplemde: () => import(/* webpackChunkName: "vue-simplemde" */'vue-simplemde'),
    FormMarkdownWrapper
  },
  data (props) {
    return {
      textareaFocused: null,
      error: false,
      editor: null,
      config: {
        autoDownloadFontAwesome: false,
        spellChecker: false,
        status: false,
        forceSync: true,
        toolbarTips: false,
        initialValue: props.value,
        placeholder: props.placeholder,
        toolbar: false,
        previewRender
      }
    }
  },

  async created () {
    const simplemde = await SimpleMDE()
    this.config.toolbar = toolbar(simplemde, this)
  },

  watch: {
    innerValue: 'handleTextareaBlur'
  },

  computed: {
    ...mapGetters({
      userId: 'userDataStore/userId',
      selectedContext: 'contextStore/selectedContext',
      applicationId: 'contextStore/applicationId',
      trackerTypeId: 'contextStore/trackerTypeId',
      trackerId: 'contextStore/trackerId',
      loading: 'loaderStore/loading'
    }),

    userCanAddAttachment () {
      const mode = this.selectedContext.mode

      return mode !== 'insert' && // #16112 disabilitati attachments per form in insert mode
        Configuration.auth.userCanAccessFeature(
          this.selectedContext, 'attachment', 'post', this.trackerDTO, this.userId
        )
    },

    textareaFieldset () {
      return this.textarea && this.textarea.$el.querySelector('fieldset')
    },

    showPlaceholder () {
      return this.textareaFocused
    }
  },

  methods: {
    ...mapActions({
      'notifyError': 'notificationStore/notifyError',
      'startLoader': 'loaderStore/startLoader',
      'stopLoader': 'loaderStore/stopLoader'
    }),
    handleTextareaFocus () {
      if (!this.textareaFocused) {
        this.textareaFocused = true
      }
      if (!this.loading['markdown-attachment']) {
        this.editor.codemirror.focus()
        this.editor.codemirror.refresh()
      }
    },
    handleTextareaBlur () {
      if (this.textareaFocused && !this.error) {
        this.textareaFieldset.classList.add('primary--text')
      }
    },
    handleError (error) {
      if (error) {
        this.error = true
        this.textareaFieldset.classList.remove('primary--text')
      } else {
        this.error = false
        this.handleTextareaBlur()
      }
    },
    handleBlur () {
      if (this.textareaFocused) {
        this.textareaFocused = false
        this.textareaFieldset.classList.remove('primary--text')
      }
    },
    handleBeforeChange (cm, obj) {
      if (this.editor.isPreviewActive()) {
        obj.cancel()
      }
    },
    init () {
      this.editor = this.$refs.markdownEditor.simplemde
      this.textarea = this.$refs.markdownTextarea
      this.editor.codemirror.on('beforeChange', this.handleBeforeChange)
      this.editor.codemirror.refresh()
    },
    uploadImage () {
      document.getElementById('imageUploader').click()
    },
    async onFilePicked () {
      this.handleBlur()
      this.startLoader({ id: 'markdown-attachment' })
      this.editor.codemirror.setOption('readOnly', 'nocursor')
      const input = this.$refs.imageUploader
      let formData = new FormData()
      formData.append('files[0]', input.files[0])

      const url = Vue.axios.agilisUrlTrackerAttachments(
        this.applicationId,
        this.trackerTypeId,
        this.trackerId)

      const requestConfig = Vue.axios.agilisRequestConfig(url)

      try {
        const response = await Vue.axios.post(url, formData, requestConfig)
        const doc = this.editor.codemirror.getDoc()
        const fileName = response.data[0].filename
        const id = response.data[0].id
        const cursor = doc.getCursor()
        doc.replaceRange(`![${fileName}](${id})`, cursor)
      } catch (error) {
        const exceptionDetails = this.$t('error.uploadFailed', { filename: input.files[0].name })
        const errorText = { response: { data: { error: { exceptionDetails } } } }
        const err = L.merge(error, errorText)
        this.notifyError(err)
      } finally {
        this.stopLoader({ id: 'markdown-attachment' })
        this.editor.codemirror.setOption('readOnly', false)
        input.value = null
      }
    }
  }
}
